import AlarmAPI from '../AlarmAPI';
import { UseQuerySubscription } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useMemo } from 'react';
import {
  ALARMS_POLLING_INTERVAL,
  AlarmsQueryAPIResponse,
  AlarmsQueryParamsAPIRequest,
  AlarmState,
  excludedEventIdsStr,
} from '../Alarm.model';
import useMyWorkspacesChildren from '../../workspace/hooks/useMyWorkspacesChildren';
import useMultipleQueries from '../../../shared/util/rtk/useQueries';
import { mergeWith } from 'lodash';
import orComparator from '../../../shared/util/lodash/merge/orComparator';

type Options = Parameters<UseQuerySubscription<any>>[1];

function useAlarmsByWorkspaces(
  params?: AlarmsQueryParamsAPIRequest,
  options: Options = {}
) {
  const { data: workspaces, ...restChildrenResponse } =
    useMyWorkspacesChildren(options);
  const keysWorkspaces = useMemo(
    () => Object.keys(workspaces || {}),
    [workspaces]
  );

  const paramsKey = JSON.stringify(params);
  const args = useMemo(
    () =>
      keysWorkspaces.map((workspaceId) => ({
        workspaceId,
        params: {
          state: AlarmState.NEW,
          page_size: 1000,
          excluded_event_ids: excludedEventIdsStr,
          ...params,
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keysWorkspaces, paramsKey]
  );

  const alarmsResponses = useMultipleQueries<
    AlarmsQueryAPIResponse,
    { workspaceId?: string; params?: AlarmsQueryParamsAPIRequest }
  >(AlarmAPI.endpoints.getFlexeserveIncidentByWorkspaceId, args, {
    pollingInterval: ALARMS_POLLING_INTERVAL,
    ...options,
    skip: !keysWorkspaces?.length && options.skip,
  });

  const dataReduced = useMemo(() => {
    const allAlarms = alarmsResponses?.data
      ?.map((response) => response.data)
      .flat();
    const data = allAlarms ?? [];
    const count =
      alarmsResponses?.data
        ?.map((response) => response.count)
        .reduce((acc, count) => (acc ?? 0) + (count ?? 0), 0) ?? 0;

    return {
      allAlarms,
      data,
      count,
    };
  }, [alarmsResponses]);

  return {
    ...mergeWith({}, restChildrenResponse, alarmsResponses, orComparator),
    ...dataReduced,
  };
}

export default useAlarmsByWorkspaces;
